import { useEffect, useState, type RefObject } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';

import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';

const ignoreEventWhenSlider = (e: KeyboardEvent) =>
  (e.target as HTMLElement).className.includes('slider');

const VIDEO_FPS = 24;

export default function SceneVideoDownloadControls({
  videoRef,
  videoUrl,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const updateCurrentTime = () => {
        if (video.paused) return;
        setCurrentTime(video.currentTime);
      };

      const onLoadedMetadata = () => {
        setTotalTime(video.duration);
        setIsMetadataLoaded(true);
      };

      video.onplay = () => setIsPlaying(true);
      video.onpause = () => setIsPlaying(false);

      video.addEventListener('loadedmetadata', onLoadedMetadata);
      const interval = setInterval(updateCurrentTime, 1000 / VIDEO_FPS);

      return () => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        setIsMetadataLoaded(false);
        clearInterval(interval);
      };
    }
  }, [videoUrl]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const skipTime = (seconds: number) => {
    const video = videoRef.current;
    if (video && isMetadataLoaded) {
      const newTime = Math.max(
        0,
        Math.min(video.currentTime + seconds, totalTime),
      );
      video.currentTime = newTime;
      setCurrentTime(video.currentTime);
    }
  };

  useHotkeys('space', togglePlayPause, { preventDefault: true });
  useHotkeys(Key.ArrowLeft, () => skipTime(-1 / VIDEO_FPS), {
    preventDefault: true,
    ignoreEventWhen: ignoreEventWhenSlider,
  });
  useHotkeys(Key.ArrowRight, () => skipTime(1 / VIDEO_FPS), {
    preventDefault: true,
    ignoreEventWhen: ignoreEventWhenSlider,
  });
  useHotkeys(`shift+${Key.ArrowLeft}`, () => skipTime(-10 / VIDEO_FPS), {
    preventDefault: true,
    ignoreEventWhen: ignoreEventWhenSlider,
  });
  useHotkeys(`shift+${Key.ArrowRight}`, () => skipTime(10 / VIDEO_FPS), {
    preventDefault: true,
    ignoreEventWhen: ignoreEventWhenSlider,
  });

  return (
    <>
      <Slider
        currentValue={currentTime * 100}
        max={totalTime * 100}
        onChange={(value) => {
          const video = videoRef.current;
          if (video) {
            video.currentTime = value / 100;
            setCurrentTime(video.currentTime);
          }
        }}
      />
      <div className="flex justify-between gap-4 pt-4">
        <div className="flex w-12 items-center">
          <Text className="text-gray-60" variant="studio/body/sm">
            {currentTime.toFixed(2)}
          </Text>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="tertiary"
            icon={<ChevronLeftIcon />}
            onClick={() => skipTime(-1 / VIDEO_FPS)}
          />
          <GoButton
            icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
            onClick={togglePlayPause}
          />
          <Button
            variant="tertiary"
            icon={<ChevronRightIcon />}
            onClick={() => skipTime(1 / VIDEO_FPS)}
          />
        </div>
        <div className="flex items-center">
          <Text className="text-gray-60" variant="studio/body/sm">
            {totalTime.toFixed(2)}
          </Text>
        </div>
        {isMetadataLoaded && (
          <div className="hidden" id="download-video-ready"></div>
        )}
      </div>
    </>
  );
}

const ChevronRightIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.3208 10.0068C15.3208 10.1507 15.2931 10.2835 15.2378 10.4053C15.1825 10.527 15.0994 10.6432 14.9888 10.7539L8.52246 17.0791C8.33984 17.2673 8.11296 17.3613 7.8418 17.3613C7.66471 17.3613 7.50146 17.3171 7.35205 17.2285C7.20264 17.1455 7.08366 17.0293 6.99512 16.8799C6.91211 16.736 6.87061 16.5755 6.87061 16.3984C6.87061 16.1328 6.97021 15.8976 7.16943 15.6929L12.9966 10.0068L7.16943 4.3208C6.97021 4.11605 6.87061 3.88363 6.87061 3.62354C6.87061 3.44092 6.91211 3.27767 6.99512 3.13379C7.08366 2.98991 7.20264 2.87646 7.35205 2.79346C7.50146 2.70492 7.66471 2.66064 7.8418 2.66064C8.11296 2.66064 8.33984 2.75195 8.52246 2.93457L14.9888 9.25977C15.0994 9.37044 15.1825 9.48665 15.2378 9.6084C15.2931 9.73014 15.3208 9.86296 15.3208 10.0068Z"
        fill="currentColor"
      />
    </svg>
  );
};
